import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
  arrayRemove,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../firebase';
import { createNotification } from './useNotifications';

/**
 * Generate a deterministic conversation ID from two UIDs
 */
export const getConversationId = (uid1, uid2) => [uid1, uid2].sort().join('_');

/**
 * Real-time listener for user's conversations
 */
export const useMyConversations = (uid) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setConversations([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', uid),
      orderBy('updatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setConversations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching conversations:', err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [uid]);

  return { conversations, loading };
};

/**
 * Real-time listener for a single conversation
 */
export const useConversation = (conversationId) => {
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!conversationId) {
      setLoading(false);
      return;
    }

    const docRef = doc(db, 'conversations', conversationId);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setConversation({ id: docSnap.id, ...docSnap.data() });
        } else {
          setConversation(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching conversation:', err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [conversationId]);

  return { conversation, loading };
};

/**
 * Real-time listener for messages in a conversation
 */
export const useConversationMessages = (conversationId) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching messages:', err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [conversationId]);

  return { messages, loading };
};

/**
 * Real-time unread conversation count for badge
 */
export const useUnreadConversationCount = (uid) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!uid) {
      setCount(0);
      return;
    }

    const q = query(
      collection(db, 'conversations'),
      where('unreadBy', 'array-contains', uid)
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setCount(snap.size);
      },
      (err) => {
        console.error('Error fetching unread conversation count:', err);
      }
    );

    return unsubscribe;
  }, [uid]);

  return count;
};

/**
 * Get or create a conversation between two users
 */
export const getOrCreateConversation = async (currentUid, otherUid, currentUserData, otherUserData) => {
  const conversationId = getConversationId(currentUid, otherUid);
  const docRef = doc(db, 'conversations', conversationId);

  try {
    // Try to create the conversation. If it already exists, setDoc without
    // merge will fail with permission denied (since it becomes an update
    // attempt matching the create rule). That's fine — we just navigate.
    await setDoc(docRef, {
      participants: [currentUid, otherUid],
      participantData: {
        [currentUid]: {
          username: currentUserData.username || 'User',
          avatarUrl: currentUserData.avatarUrl || '',
          isFullyAnonymous: currentUserData.isFullyAnonymous || false,
        },
        [otherUid]: {
          username: otherUserData.username || 'User',
          avatarUrl: otherUserData.avatarUrl || '',
          isFullyAnonymous: otherUserData.isFullyAnonymous || false,
        },
      },
      lastMessage: null,
      unreadBy: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    // Doc likely already exists — that's fine, we'll navigate to it
    console.log('Conversation may already exist:', err.message);
  }

  return conversationId;
};

/**
 * Send a message in a conversation
 */
export const sendMessage = async (conversationId, { senderUid, senderName, message, recipientUid }) => {
  // Check if recipient already has unread messages in this conversation
  const convSnap = await getDoc(doc(db, 'conversations', conversationId));
  const alreadyUnread = convSnap.exists() && (convSnap.data().unreadBy || []).includes(recipientUid);

  await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
    senderUid,
    senderName,
    message,
    createdAt: serverTimestamp(),
  });

  await updateDoc(doc(db, 'conversations', conversationId), {
    lastMessage: {
      text: message,
      senderUid,
      createdAt: new Date(),
    },
    unreadBy: arrayUnion(recipientUid),
    updatedAt: serverTimestamp(),
  });

  // Only send notification if recipient doesn't already have unread messages
  // in this conversation (avoids spamming for consecutive messages)
  if (!alreadyUnread) {
    try {
      await createNotification(
        recipientUid,
        'new_message',
        `New message from ${senderName}`,
        message.length > 100 ? message.substring(0, 100) + '...' : message,
        null,
        null,
        null,
        `/messages/${conversationId}`
      );
    } catch (err) {
      console.error('Error creating message notification:', err);
    }
  }
};

/**
 * Mark conversation as read for a user
 */
export const markConversationAsRead = async (conversationId, uid) => {
  await updateDoc(doc(db, 'conversations', conversationId), {
    unreadBy: arrayRemove(uid),
  });
};
